import React, { useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import useComponentVisible from '../../UI/ComponentVisible';

const ScheduleFilter = ({
    locations = [],
    selectedLocationKeys = [],
    setSelectedLocationKeys,
}) => {
    const { ref: dropdownRef, isComponentVisible: isDropdownOpen, setIsComponentVisible: setIsDropdownOpen } = useComponentVisible(false);

    const dropdownLabel = useMemo(() => {
        if (locations.length === 0) return 'No location fields';
        if (selectedLocationKeys.length === 0) return 'Lanes: Select fields';
        if (selectedLocationKeys.length === locations.length && locations.length > 1) {
            return `Lanes: All fields (${locations.length})`;
        }
        if (selectedLocationKeys.length === 1) {
            const firstDef = locations.find((l) => l.key === selectedLocationKeys[0]);
            return `Lanes: ${firstDef?.label || selectedLocationKeys[0]}`;
        }
        return `Lanes: ${selectedLocationKeys.length} fields`;
    }, [locations, selectedLocationKeys]);

    const defaultKey = locations[0]?.key;
    const isAllSelected = locations.length > 0 && selectedLocationKeys.length === locations.length;

    const toggleField = (key) => {
        setSelectedLocationKeys((prev) => {
            const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
            if (next.length === 0 && defaultKey) {
                return [defaultKey];
            }
            return next;
        });
    };

    return (
        <div className="flex items-center gap-3">
            <div className="relative" ref={dropdownRef}>
                <button
                    className="stride-filter-button"
                    type="button"
                    onClick={() => setIsDropdownOpen((prev) => !prev)}
                    aria-expanded={isDropdownOpen}
                >
                    <span>{dropdownLabel}</span>
                    <ChevronDown size={15} />
                </button>

                {isDropdownOpen && (
                    <div className="stride-dropdown-menu right-0 min-w-[230px] max-w-[320px] p-1.5 max-h-[280px] overflow-y-auto">
                        <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-border-subtle mb-1 text-[11px] font-bold text-stride-muted uppercase tracking-wider">
                            <span>Location fields</span>
                            {locations.length > 1 && (
                                <div className="flex items-center gap-2">
                                    {!isAllSelected && (
                                        <button
                                            type="button"
                                            className="text-[11px] font-semibold text-primary hover:underline cursor-pointer bg-transparent border-0 p-0"
                                            onClick={() => setSelectedLocationKeys(locations.map((l) => l.key))}
                                        >
                                            Select all
                                        </button>
                                    )}
                                    {selectedLocationKeys.length > 1 && (
                                        <button
                                            type="button"
                                            className="text-[11px] font-semibold text-primary hover:underline cursor-pointer bg-transparent border-0 p-0"
                                            onClick={() => setSelectedLocationKeys(defaultKey ? [defaultKey] : [])}
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        {locations.length === 0 ? (
                            <div className="p-3 text-xs text-gray-400 text-center">No location fields configured</div>
                        ) : (
                            locations.map((loc) => {
                                const isChecked = selectedLocationKeys.includes(loc.key);
                                return (
                                    <label
                                        key={loc._id || loc.key}
                                        className={`flex items-center gap-2 w-full px-2.5 py-2 rounded-md text-[13px] text-stride-body text-left hover:bg-surface-alt cursor-pointer transition-colors ${isChecked ? 'bg-primary-soft font-semibold text-primary' : ''}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleField(loc.key)}
                                            className="m-0 cursor-pointer accent-primary rounded"
                                        />
                                        <span>{loc.label}</span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ScheduleFilter;
